import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Grid,
  Typography,
  IconButton,
  FormHelperText,
  Divider,
  SelectChangeEvent
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { BusinessLocation } from '../../firebase/services/locationService';

interface LocationFormProps {
  open: boolean;
  onClose: () => void;
  onSave: (locationData: Omit<BusinessLocation, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  initialData?: BusinessLocation;
  title: string;
}

const initialLocationState: Omit<BusinessLocation, 'id' | 'createdAt' | 'updatedAt'> = {
  name: '',
  type: 'Marina',
  address: '',
  city: '',
  state: '',
  zipCode: '',
  phone: '',
  email: '',
  website: '',
  manager: '',
  description: '',
  primaryLocation: false,
  active: true
};

const LocationForm: React.FC<LocationFormProps> = ({ 
  open, 
  onClose, 
  onSave, 
  initialData, 
  title 
}) => {
  const [locationData, setLocationData] = useState<Omit<BusinessLocation, 'id' | 'createdAt' | 'updatedAt'>>(initialLocationState);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (initialData) {
      // Convert any Timestamp fields to Date if needed
      setLocationData({
        name: initialData.name,
        type: initialData.type,
        address: initialData.address,
        city: initialData.city,
        state: initialData.state,
        zipCode: initialData.zipCode,
        phone: initialData.phone || '',
        email: initialData.email || '',
        website: initialData.website || '',
        manager: initialData.manager || '',
        description: initialData.description || '',
        primaryLocation: initialData.primaryLocation,
        active: initialData.active,
        coordinates: initialData.coordinates,
        operatingHours: initialData.operatingHours
      });
    } else {
      setLocationData(initialLocationState);
    }
    setErrors({});
  }, [initialData, open]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target;
    if (name) {
      setLocationData(prev => ({ ...prev, [name]: value }));
      if (errors[name]) {
        setErrors(prev => ({ ...prev, [name]: '' }));
      }
    }
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setLocationData(prev => ({ ...prev, [name as string]: value }));
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setLocationData(prev => ({ ...prev, [name]: checked }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!locationData.name.trim()) {
      newErrors.name = 'Location name is required';
    }
    
    if (!locationData.address.trim()) {
      newErrors.address = 'Address is required';
    }
    
    if (!locationData.city.trim()) {
      newErrors.city = 'City is required';
    }
    
    if (!locationData.state.trim()) {
      newErrors.state = 'State is required';
    }
    
    if (!locationData.zipCode.trim()) {
      newErrors.zipCode = 'Zip code is required';
    }
    
    if (locationData.email && !/\S+@\S+\.\S+/.test(locationData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (locationData.website && !/^(https?:\/\/)?(www\.)?[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(\/\S*)?$/.test(locationData.website)) {
      newErrors.website = 'Please enter a valid website URL';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    try {
      await onSave(locationData);
      onClose();
    } catch (error) {
      console.error('Error saving location:', error);
      // Could display an error message here
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          {title}
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box component="form" noValidate>
          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Basic Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                name="name"
                label="Location Name"
                fullWidth
                required
                value={locationData.name}
                onChange={handleChange}
                error={!!errors.name}
                helperText={errors.name}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Location Type</InputLabel>
                <Select
                  name="type"
                  value={locationData.type}
                  onChange={handleSelectChange}
                  label="Location Type"
                >
                  <MenuItem value="Marina">Marina</MenuItem>
                  <MenuItem value="Store">Store</MenuItem>
                  <MenuItem value="Kitchen">Kitchen</MenuItem>
                  <MenuItem value="Warehouse">Warehouse</MenuItem>
                  <MenuItem value="Office">Office</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                name="description"
                label="Description"
                fullWidth
                multiline
                rows={2}
                value={locationData.description}
                onChange={handleChange}
              />
            </Grid>
            
            {/* Address Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
                Address Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                name="address"
                label="Street Address"
                fullWidth
                required
                value={locationData.address}
                onChange={handleChange}
                error={!!errors.address}
                helperText={errors.address}
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                name="city"
                label="City"
                fullWidth
                required
                value={locationData.city}
                onChange={handleChange}
                error={!!errors.city}
                helperText={errors.city}
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                name="state"
                label="State"
                fullWidth
                required
                value={locationData.state}
                onChange={handleChange}
                error={!!errors.state}
                helperText={errors.state}
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                name="zipCode"
                label="Zip Code"
                fullWidth
                required
                value={locationData.zipCode}
                onChange={handleChange}
                error={!!errors.zipCode}
                helperText={errors.zipCode}
              />
            </Grid>
            
            {/* Contact Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
                Contact Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                name="phone"
                label="Phone Number"
                fullWidth
                value={locationData.phone}
                onChange={handleChange}
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                name="email"
                label="Email Address"
                fullWidth
                value={locationData.email}
                onChange={handleChange}
                error={!!errors.email}
                helperText={errors.email}
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                name="website"
                label="Website"
                fullWidth
                value={locationData.website}
                onChange={handleChange}
                error={!!errors.website}
                helperText={errors.website}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                name="manager"
                label="Location Manager"
                fullWidth
                value={locationData.manager}
                onChange={handleChange}
              />
            </Grid>
            
            {/* Status Settings */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
                Status Settings
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={locationData.primaryLocation}
                    onChange={handleSwitchChange}
                    name="primaryLocation"
                    color="primary"
                  />
                }
                label="Primary Location"
              />
              <FormHelperText>
                Only one location can be set as primary. Setting this one as primary will update others.
              </FormHelperText>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={locationData.active}
                    onChange={handleSwitchChange}
                    name="active"
                    color="primary"
                  />
                }
                label="Active"
              />
              <FormHelperText>
                Inactive locations won't be shown in general location lists but can still be accessed directly.
              </FormHelperText>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          color="primary"
          disabled={loading}
        >
          {loading ? 'Saving...' : 'Save Location'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LocationForm; 